import { connect } from 'react-redux'
import { compose, withHandlers, withState, withProps, lifecycle } from 'recompose'
import { bindActionCreators } from 'redux'
import { List } from 'immutable'

import { makeHasGate } from 'app/modules/gates/selectors'
import * as PlaybookSelectors from 'app/modules/playbook/selectors'
import * as PlaybookActions from 'app/modules/playbook/actions'
import { ConditionTypesByCategory, ConditionTypes, ConditionType, ConditionCategory } from 'app/modules/playbook/constants'
import * as DemandbaseEnrichmentSelectors from 'app/modules/enrichment/demandbase/selectors'
import { fetchDemandbaseTargetingIndustries, fetchDemandbaseTargetingSubIndustries } from 'app/modules/enrichment/demandbase/actions'
import { getClearbitRevealContext } from 'app/modules/enrichment/clearbit/selectors'
import { isConnectedTo } from 'app/modules/integrations/selectors'

import ACCESS from 'utils/Access'
import { isIntegrationConnected } from 'utils/Integrations'


import ConditionModal from './component'
import { checkUrlValidity } from './helpers'

const mapStateToProps = state => {
  const id = PlaybookSelectors.getCurrentConditionId(state)
  const revealContext = getClearbitRevealContext(state)
  const hasDemandbase = isConnectedTo(state, 'demandbase')
  return {
    id,
    condition: PlaybookSelectors.getCondition(state, { id }),
    isOutboundPlaybook: PlaybookSelectors.isOutboundPlaybook(state),
    hasMarketingSequences: makeHasGate('has_marketing_sequences')(state),
    hasSegmentSelect: makeHasGate('has_segment_display_targeting')(state),
    hasDemandbase,
    showEnrichmentUpgrade: revealContext.isEligibleForTargetingUpgradeFlow && !hasDemandbase,
    externalData: {
      demandbaseIndustries: DemandbaseEnrichmentSelectors.demandbaseTargetingIndustries(state),
      demandbaseSubIndustries: DemandbaseEnrichmentSelectors.demandbaseTargetingSubIndustries(state)
    }
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    setValue: PlaybookActions.setConditionValue,
    setOperator: PlaybookActions.setConditionOperator,
    setField: PlaybookActions.setConditionField,
    hideConditionModal: PlaybookActions.hideConditionModal,
    deleteCondition: PlaybookActions.deleteCondition,
    fetchDemandbaseIndustries: fetchDemandbaseTargetingIndustries,
    fetchDemandbaseSubIndustries: fetchDemandbaseTargetingSubIndustries
  }, dispatch),
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withState('testUrl', 'setTestUrl', ''),
  withState('testUrlIsValid', 'setTestUrlIsValid', false),
  withProps(props => ({
    checkTestUrlValidity: (testUrl, newCondition = {}) => {
      let testAgainstUrl = testUrl
      if (!!testAgainstUrl && !testAgainstUrl.startsWith('http')) {
        testAgainstUrl = `https://${testAgainstUrl}`
      }
      const { condition } = props
      const evaluateAgainst = condition.merge(newCondition)
      const { value, operator, field } = evaluateAgainst
      const anchor = document.createElement('a')
      anchor.href = testAgainstUrl
      props.setTestUrlIsValid(checkUrlValidity(value, operator, field, anchor))
    }
  })),
  withState('selectedCategory', 'setCategory', props => {
    if (
      props.condition.field === ConditionType.URL_QUERY &&
      props.condition.value.some(v => v && v.startsWith && v.startsWith('d_campaign'))
    ) {
      return ConditionTypes.get('email_playbook').category
    } else {
      return ConditionTypes.get(props.condition.field).category
    }
  }),
  withState('selectedCategoryOptions', 'setCategoryOptions', props => (
    ConditionTypesByCategory.get(props.selectedCategory)
      .filter(({ requiredGate }) => (requiredGate ? ACCESS.hasGates(requiredGate) : true))
      .filter(({ requiredIntegration }) => (requiredIntegration ? isIntegrationConnected(requiredIntegration) : true))
      .filter(({ outboundOnly }) => (outboundOnly ? props.isOutboundPlaybook : true))
      .map(({ displayName }, type) => ({ value: type, label: displayName }))
      .toArray()
  )),
  withHandlers({
    onOperatorChange: ({ id, actions: { setOperator }, testUrl, selectedCategory, checkTestUrlValidity }) => ({ value: operator }) => {
      setOperator({ operator, id })
      if (selectedCategory === ConditionCategory.URL) {
        checkTestUrlValidity(testUrl, { operator })
      }
    },
    onTestUrlChange: props => event => {
      props.setTestUrl(event.currentTarget.value)
      props.checkTestUrlValidity(event.currentTarget.value || '')
    },
    onCategoryClick: ({ setCategory, setCategoryOptions, id, actions: { setField }, isOutboundPlaybook }) => category => () => {
      const categoryOptions = ConditionTypesByCategory.get(category)
        .filter(({ requiredGate }) => (requiredGate ? ACCESS.hasGates(requiredGate) : true))
        .filter(({ requiredIntegration }) => (requiredIntegration ? isIntegrationConnected(requiredIntegration) : true))
        .filter(({ outboundOnly }) => (outboundOnly ? isOutboundPlaybook : true))
        .map(({ displayName }, type) => ({ value: type, label: displayName }))
        .toArray()

      const field = categoryOptions[0].value

      setCategory(category)
      setCategoryOptions(categoryOptions)
      setField({ field, id })
    },
    onValueChange: ({ id, actions: { setValue }, selectedCategory, testUrl, checkTestUrlValidity }) => value => {
      setValue({ value, id })
      if (selectedCategory === ConditionCategory.URL) {
        checkTestUrlValidity(testUrl, { value })
      }
    },
    onFieldChange: ({ id, actions: { setField }, selectedCategory, testUrl, checkTestUrlValidity }) => ({ value: field }) => {
      setField({ field, id })
      if (selectedCategory === ConditionCategory.URL) {
        checkTestUrlValidity(testUrl, { field })
      }
    },
    onCloseClick: ({ actions: { hideConditionModal, deleteCondition, setValue }, id, condition: { value }}) => () => {
      const emptyValues = ["", null, undefined]
      // We want to make sure the user doesn't shoot themselves in the foot
      // either by having one or more values be "empty" (when it's a List) or
      // having the singular value be an empty value
      if (List.isList(value)) {
        if (value.every(v => emptyValues.includes(v))) {
          deleteCondition(id)
        } else {
          setValue({ id, value: value.filter(v => !emptyValues.includes(v)) })
        }
      } else if (emptyValues.includes(value)) {
        deleteCondition(id)
      }

      hideConditionModal()
    },
    onSegmentChange: ({ id, actions: { setValue }}) => value => {
      setValue({ value: List(value.map(option => option.value)), id })
    },
    onSegmentNext: ({ id, actions: { setValue, hideConditionModal }}) => (userListIds) => {
      setValue({ value: userListIds, id })
      hideConditionModal()
    },
  }),
  lifecycle({
    componentDidMount() {
      const { hasDemandbase, actions } = this.props

      if (hasDemandbase) {
        actions.fetchDemandbaseIndustries()
        actions.fetchDemandbaseSubIndustries()
      }
    }
  })
)(ConditionModal)



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/ConditionModal/index.js